import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import Radio from '@groovehq/internal-design-system/lib/components/Radio/Radio'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import { doBuildInboxMenuFromMailboxes } from 'ducks/folders/operations/collections'
import { doGmailImport } from 'ducks/mailboxes/actions'
import { doOpenSearchPageWithMailboxAndFolder } from 'ducks/channels/actions'
import { selectIsInInbox, selectIsInSettings } from 'selectors/location'
import { capitalize } from 'util/strings'
import { useChannel } from 'ducks/channels/hooks'

const FORM_SCHEMA = yup.object().shape({
  import_history: yup.boolean(),
  import_labels: yup.boolean(),
  close_tickets: yup.boolean(),
})

const formEmptyState = {
  // defaultValue has to match yup form schema
  import_history: true,
  import_labels: true,
  close_tickets: false,
}

const MailImport = ({
  onClose,
  onExit,
  drawerChannelType,
  drawerResourceId: channelId,
  drawerId,
}) => {
  const [isSaving, setIsSaving] = useState(null)
  const [errors, setErrors] = useState(null)

  const dispatch = useDispatch()
  const isInInbox = useSelector(selectIsInInbox)
  const isInSettings = useSelector(selectIsInSettings)

  const useFormMethods = useForm({
    mode: 'all',
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: formEmptyState,
  })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useFormMethods

  const isImportingHistory = watch('import_history')
  const isCloseTickets = watch('close_tickets')

  const { channel } = useChannel(channelId)
  const { folders } = channel || {}

  const handleOnExit = useCallback(
    () => {
      reset(formEmptyState)
      onExit()
    },
    [onExit, reset]
  )

  const handleExitAndRedirect = useCallback(
    () => {
      onExit()
      dispatch(
        doOpenSearchPageWithMailboxAndFolder(
          channelId,
          folders ? folders[0].id : null
        )
      )
    },
    [dispatch, onExit, channelId, folders]
  )

  const handleRebuildExitAndRedirect = useCallback(
    () => {
      dispatch(doBuildInboxMenuFromMailboxes())
      handleExitAndRedirect()
    },
    [dispatch, handleExitAndRedirect]
  )

  const onSubmit = useCallback(
    async data => {
      try {
        setIsSaving(true)
        setErrors(null)
        await dispatch(doGmailImport(channelId, data))
        setIsSaving(false)
        if (isInInbox || isInSettings) {
          handleRebuildExitAndRedirect()
        } else {
          handleExitAndRedirect()
        }
      } catch (error) {
        setIsSaving(false)
        setErrors(error)
      }
    },
    [
      dispatch,
      channelId,
      handleRebuildExitAndRedirect,
      isInInbox,
      isInSettings,
      handleExitAndRedirect,
    ]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const formId = `${drawerId}-form`

  const footer = (
    <ModalBtns
      saveBtnText={
        <>
          {!isSaving && (
            <span>Finish &amp; take me to my {app.t('mailbox')}</span>
          )}
          {isSaving && (
            <span>
              Saving<AnimatedEllipsis />
            </span>
          )}
        </>
      }
      saveBtnHtmlType="submit"
      saveBtnDisabled={!isValid || isSaving}
      saveBtnForm={formId}
      saveBtnTestId="finish-and-redirect-inbox"
      tertiaryBtnText="Back"
      onClickTertiaryBtn={handleBack}
      tertiaryBtnDisabled={isSaving}
    />
  )

  return (
    <AdminAccessDrawer
      title="Import existing emails"
      footer={footer}
      onClose={handleOnExit}
      data-test-id="channels-mail-import"
    >
      <HeaderWithImage
        className="grui mt-14 mb-10"
        intro={`Import the last 50 conversations from your ${capitalize(
          drawerChannelType
        )} account into this ${app.t('mailbox')}.`}
      />
      <MessageCard type="negative" className="grui mb-12" visible={!!errors}>
        An error occurred importing your conversations. Please try again.
      </MessageCard>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div>
            <Checkbox id="import_history" {...register('import_history')}>
              Import emails
            </Checkbox>
            <div className="grui mt-5 ml-12">
              {isImportingHistory && (
                <div className="grui mt-5">
                  <Radio
                    {...register('close_tickets')}
                    id="conversationOpenedOption"
                    name="close_tickets"
                    value={false}
                    defaultChecked={!isCloseTickets}
                  >
                    Import these conversations as Open
                  </Radio>
                  <Radio
                    {...register('close_tickets')}
                    id="conversationClosedOption"
                    className="grui mt-3"
                    name="close_tickets"
                    value
                    defaultChecked={isCloseTickets}
                  >
                    Import these conversations as Closed
                  </Radio>
                </div>
              )}
            </div>
          </div>
          {isImportingHistory && (
            <div className="grui mt-12">
              <Checkbox id="import_labels" {...register('import_labels')}>
                Import labels as tags in Groove
              </Checkbox>
            </div>
          )}
        </div>
      </form>
    </AdminAccessDrawer>
  )
}

export default MailImport
